import { useState, useCallback, useMemo, useReducer, useRef } from 'react';
import { toast } from 'sonner';
import {
  formBuilderApi,
  useCarrierSummaries,
  useCarrierForms,
  useSaveForm,
  formBuilderKeys,
  type FormListItem,
} from '@/features/quote-and-apply-builder';
import { useQueryClient } from '@tanstack/react-query';
import { fundingPolicyTypeDefList, FundingPolicyType, type FundingPolicyTypeDef } from '@shared';

const ADD_NEW_VALUE = '__add_new__';

interface EditFormState {
  title: string;
  formType: PolicyFormType;
  selectedCarrierId: string;
  customCarrier: string;
  isCustomCarrier: boolean;
  selectedProduct: string;
  customProduct: string;
  isCustomProduct: boolean;
  email: string;
  selectedPolicyTypes: FundingPolicyType[];
  policyTypeFilter: string;
  stateCode: string;
  importedDefinition: PolicyFormDefinition | null;
  importSource: string;
  importError: string;
  saveError: string;
}

type EditFormAction =
  | { type: 'SET_TITLE'; value: string }
  | { type: 'SET_FORM_TYPE'; value: PolicyFormType }
  | { type: 'SET_EMAIL'; value: string }
  | { type: 'SET_STATE_CODE'; value: string }
  | { type: 'SET_POLICY_TYPE_FILTER'; value: string }
  | { type: 'SET_CUSTOM_CARRIER'; value: string }
  | { type: 'SET_CUSTOM_PRODUCT'; value: string }
  | { type: 'SELECT_EXISTING_CARRIER' }
  | { type: 'SELECT_EXISTING_PRODUCT' }
  | { type: 'SELECT_CARRIER'; value: string }
  | { type: 'SELECT_ADD_NEW_CARRIER' }
  | { type: 'SELECT_PRODUCT'; value: string }
  | { type: 'SELECT_ADD_NEW_PRODUCT' }
  | { type: 'TOGGLE_POLICY_TYPE'; value: FundingPolicyType }
  | { type: 'IMPORT_SUCCESS'; definition: PolicyFormDefinition; source: string; metadata?: { title?: string; formType?: PolicyFormType; carrier?: string; product?: string } }
  | { type: 'IMPORT_ERROR'; message: string }
  | { type: 'CLEAR_IMPORT' }
  | { type: 'SAVE_ERROR'; message: string }
  | { type: 'RESET'; state: EditFormState };

const initialState: EditFormState = {
  title: '',
  formType: 'application-form',
  selectedCarrierId: '',
  customCarrier: '',
  isCustomCarrier: false,
  selectedProduct: '',
  customProduct: '',
  isCustomProduct: false,
  email: '',
  selectedPolicyTypes: [],
  policyTypeFilter: '',
  stateCode: '',
  importedDefinition: null,
  importSource: '',
  importError: '',
  saveError: '',
};

function editFormReducer(state: EditFormState, action: EditFormAction): EditFormState {
  switch (action.type) {
    case 'SET_TITLE':
      return { ...state, title: action.value };
    case 'SET_FORM_TYPE':
      return { ...state, formType: action.value };
    case 'SET_EMAIL':
      return { ...state, email: action.value };
    case 'SET_STATE_CODE':
      return { ...state, stateCode: action.value.toUpperCase() };
    case 'SET_POLICY_TYPE_FILTER':
      return { ...state, policyTypeFilter: action.value };
    case 'SET_CUSTOM_CARRIER':
      return { ...state, customCarrier: action.value };
    case 'SET_CUSTOM_PRODUCT':
      return { ...state, customProduct: action.value };
    case 'SELECT_EXISTING_CARRIER':
      return { ...state, isCustomCarrier: false, customCarrier: '' };
    case 'SELECT_EXISTING_PRODUCT':
      return { ...state, isCustomProduct: false, customProduct: '' };
    case 'SELECT_CARRIER':
      return {
        ...state,
        isCustomCarrier: false,
        selectedCarrierId: action.value,
        selectedProduct: '',
        isCustomProduct: false,
        customProduct: '',
      };
    case 'SELECT_ADD_NEW_CARRIER':
      return {
        ...state,
        isCustomCarrier: true,
        selectedCarrierId: '',
        selectedProduct: '',
        isCustomProduct: true,
        customProduct: '',
      };
    case 'SELECT_PRODUCT':
      return { ...state, isCustomProduct: false, selectedProduct: action.value };
    case 'SELECT_ADD_NEW_PRODUCT':
      return { ...state, isCustomProduct: true, selectedProduct: '' };
    case 'TOGGLE_POLICY_TYPE':
      return {
        ...state,
        selectedPolicyTypes: state.selectedPolicyTypes.includes(action.value)
          ? state.selectedPolicyTypes.filter(pt => pt !== action.value)
          : [...state.selectedPolicyTypes, action.value],
      };
    case 'IMPORT_SUCCESS': {
      const updates: Partial<EditFormState> = {
        importedDefinition: action.definition,
        importSource: action.source,
        importError: '',
      };
      if (action.metadata) {
        if (action.metadata.title) updates.title = action.metadata.title;
        if (action.metadata.formType) updates.formType = action.metadata.formType;
      }
      return { ...state, ...updates };
    }
    case 'IMPORT_ERROR':
      return { ...state, importError: action.message };
    case 'CLEAR_IMPORT':
      return { ...state, importedDefinition: null, importSource: '', importError: '' };
    case 'SAVE_ERROR':
      return { ...state, saveError: action.message };
    case 'RESET':
      return action.state;
  }
}

export function useEditFormConfig(carrierId: string) {
  const queryClient = useQueryClient();
  const carrierIdNum = Number(carrierId);
  const [formToEdit, setFormToEdit] = useState<FormListItem | null>(null);
  const [formConfig, setFormConfig] = useState<PolicyFormConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: carriers } = useCarrierSummaries();
  const { data: forms } = useCarrierForms(carrierIdNum);
  const { mutate: saveForm, isPending: isSaving } = useSaveForm();

  const [state, dispatch] = useReducer(editFormReducer, initialState);

  const uniqueProducts = useMemo(() => {
    if (!forms) return [];
    const products = new Set(forms.map(form => form.product).filter(Boolean));
    return Array.from(products).sort();
  }, [forms]);

  const carrierName = useMemo(() => {
    if (state.isCustomCarrier) return state.customCarrier.trim();
    const carrier = carriers?.find(
      c => String(c.insuranceCarrierId) === state.selectedCarrierId,
    );
    return carrier?.carrierName ?? '';
  }, [state.isCustomCarrier, state.customCarrier, carriers, state.selectedCarrierId]);

  const productName = state.isCustomProduct ? state.customProduct.trim() : state.selectedProduct;

  const filteredPolicyTypes = useMemo(() => {
    if (!state.policyTypeFilter.trim()) return fundingPolicyTypeDefList;
    const lower = state.policyTypeFilter.toLowerCase();
    return fundingPolicyTypeDefList.filter(
      (pt: FundingPolicyTypeDef) =>
        pt.label.toLowerCase().includes(lower) ||
        (pt.carrier && pt.carrier.toLowerCase().includes(lower)) ||
        pt.description.toLowerCase().includes(lower),
    );
  }, [state.policyTypeFilter]);

  const isValid = !!(
    state.title.trim() &&
    carrierName &&
    productName &&
    state.selectedPolicyTypes.length > 0
  );

  const handleEditClick = useCallback(
    async (event: React.MouseEvent, form: FormListItem) => {
      event.stopPropagation();
      setFormToEdit(form);
      setIsLoadingConfig(true);

      try {
        const config = await formBuilderApi.getFormById(form.id);
        setFormConfig(config);

        const matchedCarrier = carriers?.find(
          c => c.carrierName === config.formMetadata.carrier,
        );

        const policyTypes = (config.fundingPolicyTypeId ?? []) as FundingPolicyType[];

        dispatch({
          type: 'RESET',
          state: {
            title: config.formMetadata.title,
            formType: config.formMetadata.formType,
            selectedCarrierId: matchedCarrier ? String(matchedCarrier.insuranceCarrierId) : '',
            customCarrier: matchedCarrier ? '' : config.formMetadata.carrier,
            isCustomCarrier: !matchedCarrier,
            selectedProduct: config.formMetadata.product,
            customProduct: '',
            isCustomProduct: false,
            email: config.formMetadata.carrierEmail || '',
            selectedPolicyTypes: policyTypes,
            policyTypeFilter: '',
            stateCode: config.stateCode || '',
            importedDefinition: null,
            importSource: '',
            importError: '',
            saveError: '',
          },
        });
      } catch {
        toast.error('Failed to load form configuration');
        setFormToEdit(null);
      } finally {
        setIsLoadingConfig(false);
      }
    },
    [carriers],
  );

  const handleFileImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const data = JSON.parse(evt.target?.result as string);
        if (data.formDefinition?.sections && data.formDefinition?.questions) {
          dispatch({
            type: 'IMPORT_SUCCESS',
            definition: data.formDefinition,
            source: file.name,
            metadata: data.formMetadata,
          });
        } else {
          dispatch({
            type: 'IMPORT_ERROR',
            message: 'Invalid format. Expected JSON with formDefinition.',
          });
        }
      } catch {
        dispatch({ type: 'IMPORT_ERROR', message: 'Failed to parse JSON file.' });
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    setFormToEdit(null);
    setFormConfig(null);
  }, []);

  const handleSave = useCallback(() => {
    if (!isValid || !formConfig) return;

    const formDefinition = state.importedDefinition ?? formConfig.formDefinition;

    const updatedMetadata: PolicyFormMetadata = {
      ...formConfig.formMetadata,
      title: state.title.trim(),
      formType: state.formType,
      carrier: carrierName,
      carrierEmail: state.email.trim(),
      product: productName,
      totalQuestions: Object.keys(formDefinition.questions).length,
    };

    saveForm(
      {
        formDefinition,
        formMetadata: updatedMetadata,
        name: state.title.trim(),
        fundingPolicyTypeId: state.selectedPolicyTypes,
        ...(state.stateCode.trim() && { stateCode: state.stateCode.trim() }),
        dryRun: false,
      },
      {
        onSuccess: () => {
          toast.success(`"${state.title.trim()}" has been updated`);
          queryClient.invalidateQueries({ queryKey: formBuilderKeys.lists() });
          handleClose();
        },
        onError: error => {
          dispatch({
            type: 'SAVE_ERROR',
            message: error instanceof Error ? error.message : 'Failed to save form',
          });
        },
      },
    );
  }, [isValid, formConfig, state, carrierName, productName, saveForm, queryClient, handleClose]);

  const handleCarrierChange = useCallback((value: string) => {
    if (value === ADD_NEW_VALUE) {
      dispatch({ type: 'SELECT_ADD_NEW_CARRIER' });
    } else {
      dispatch({ type: 'SELECT_CARRIER', value });
    }
  }, []);

  const handleProductChange = useCallback((value: string) => {
    if (value === ADD_NEW_VALUE) {
      dispatch({ type: 'SELECT_ADD_NEW_PRODUCT' });
    } else {
      dispatch({ type: 'SELECT_PRODUCT', value });
    }
  }, []);

  return {
    formToEdit,
    isLoadingConfig,
    isOpen: !!formToEdit,
    ...state,
    carrierName,
    productName,
    isSaving,
    carriers,
    uniqueProducts,
    filteredPolicyTypes,
    isValid,
    fileInputRef,
    dispatch,
    handleEditClick,
    handleClose,
    handleSave,
    handleFileImport,
    handleCarrierChange,
    handleProductChange,
  };
}
